import React, { useLayoutEffect } from "react";

import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

type Props = {
  data: {
    category: string;
    value: number;
    open: number;
    stepValue?: number;
    displayValue: string;
  }[];
};

const WaterfallChart = ({ data }: Props) => {
  useLayoutEffect(() => {
    let root = am5.Root.new("waterFallChartdiv");

    /* remove amchart logo */
    root._logo?.dispose();

    // Set themes
    root.setThemes([am5themes_Animated.new(root)]);

    // Create chart
    let chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
      })
    );

    // Create axes
    let xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });

    let xAxis = chart.xAxes.push(
      am5xy.CategoryAxis.new(root, {
        maxDeviation: 0,
        categoryField: "category",
        renderer: xRenderer,
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    xAxis.labelsContainer.set(
      "tooltip",
      am5.Tooltip.new(root, {
        pointerOrientation: "up",
      })
    );
    xAxis.get("renderer").labels.template.setAll({
      oversizedBehavior: "wrap",
      maxWidth: 80,
      textAlign: "center",
      fontSize: "12px",
      tooltipText: "{category}",
    });

    xRenderer.labels.template.setup = function (target: any) {
      target.set(
        "background",
        am5.Rectangle.new(root, {
          fill: am5.color(0x000000),
          fillOpacity: 0,
        })
      );
    };

    xRenderer.grid.template.setAll({
      location: 1,
    });

    let yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(root, {
        numberFormat: "#.a",
        maxDeviation: 0,
        min: 0,
        renderer: am5xy.AxisRendererY.new(root, { strokeOpacity: 0.1 }),
        tooltip: am5.Tooltip.new(root, {}),
      })
    );

    // Create series
    let series = chart.series.push(
      am5xy.ColumnSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "value",
        openValueYField: "open",
        categoryXField: "category",
      })
    );

    series.columns.template.setAll({
      templateField: "columnConfig",
      strokeOpacity: 0,
    });

    series.columns.template.setAll({
      tooltipText: "{category}:  {displayValue}",
      tooltipY: am5.percent(10),
      width: am5.percent(40),
    });
    series.bullets.push(function () {
      return am5.Bullet.new(root, {
        sprite: am5.Label.new(root, {
          //text: "{displayValue}",
          centerY: am5.p50,
          centerX: am5.p50,
          populateText: true,
        }),
      });
    });

    let stepSeries = chart.series.push(
      am5xy.StepLineSeries.new(root, {
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: "stepValue",
        categoryXField: "category",
        noRisers: true,
        locationX: 0.65,
        stroke: root.interfaceColors.get("alternativeBackground"),
      })
    );

    stepSeries.strokes.template.setAll({
      strokeDasharray: [3, 3],
    });

    xAxis.data.setAll(data);
    series.data.setAll(data);
    stepSeries.data.setAll(data);

    // Make stuff animate on load
    series.appear(1000);
    chart.appear(1000, 100);

    return () => {
      // Clean up any resources or event listeners here
      root.dispose();
    };
  }, [data]);

  return (
    <div id="waterFallChartdiv" style={{ width: "100%", height: "100%" }}></div>
  );
};

export default WaterfallChart;
